import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import StorefrontWrapper from "./components/StorefrontWrapper";
import IndexPage from "./pages/Index";
import Auth from "./pages/Auth";
import { CartProvider } from "./contexts/CartContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SiteThemeProvider } from "./hooks/useSiteTheme";
import SeoHead from "./components/SeoHead";
import { trackPageView } from "./lib/fbPixel";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

function lazyRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise<never>(() => {});
    })
  );
}

const StorefrontChrome = lazyRetry(() => import("./components/StorefrontChrome"));

const loadProductDetail = () => import("./pages/ProductDetail");
const loadCheckout = () => import("./pages/Checkout");
const loadCategoryPage = () => import("./pages/CategoryPage");

const ProductDetail = lazy(loadProductDetail);
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const Checkout = lazy(loadCheckout);
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const OrderProcedure = lazy(() => import("./pages/OrderProcedure"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CategoryPage = lazy(loadCategoryPage);
const AllProducts = lazy(() => import("./pages/AllProducts"));
const AllCategories = lazy(() => import("./pages/AllCategories"));
const NewArrivals = lazy(() => import("./pages/NewArrivals"));
const BestSellingPage = lazy(() => import("./pages/BestSelling"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Reviews = lazy(() => import("./pages/Reviews"));
const BabyCareTips = lazy(() => import("./pages/BabyCareTips"));
const BabyCareTipDetail = lazy(() => import("./pages/BabyCareTipDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ModeratorSignup = lazy(() => import("./pages/ModeratorSignup"));
const ModeratorPreview = lazy(() => import("./pages/ModeratorPreview"));
const ModeratorAccess = lazy(() => import("./pages/ModeratorAccess"));
const StaffSignup = lazy(() => import("./pages/StaffSignup"));
const AdminRoutes = lazy(() => import("./routes/AdminRoutes"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const PageLoader = () => {
  const location = useLocation();
  const isHeavy = location.pathname.startsWith("/admin") || location.pathname.startsWith("/moderator");

  if (!isHeavy) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="admin-theme min-h-screen bg-background flex overflow-hidden">
      <aside className="hidden lg:block w-64 shrink-0 bg-muted/40 border-r border-border" />
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        <header className="h-12 sm:h-14 border-b border-border bg-card" />
        <main className="flex-1 p-3 sm:p-4 lg:p-6">
          <div className="space-y-3 animate-pulse">
            <div className="h-8 w-52 rounded-md bg-muted" />
            <div className="h-10 w-full rounded-md bg-muted/70" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-24 rounded-lg bg-muted/70" />
              ))}
            </div>
            <div className="h-[420px] rounded-lg bg-muted/60" />
          </div>
        </main>
      </div>
    </div>
  );
};

const RouteChrome = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {!isAdminRoute && <SeoHead />}
      {!isAdminRoute && (
        <Suspense fallback={null}>
          <StorefrontChrome />
        </Suspense>
      )}
    </>
  );
};

const AppBootEffects = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isAdmin = location.pathname.startsWith("/admin");

    if (isAdmin) {
      const warmAdmin = () => {
        import("recharts").catch(() => {});
        import("jspdf").catch(() => {});
      };
      if ("requestIdleCallback" in window) {
        const id = (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number; cancelIdleCallback?: (id: number) => void }).requestIdleCallback(warmAdmin, { timeout: 3000 });
        return () => (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(id);
      }
      const timer = globalThis.setTimeout(warmAdmin, 1500);
      return () => globalThis.clearTimeout(timer);
    }

    if (location.pathname === "/auth" || location.pathname === "/reset-password") {
      return;
    }

    const preloadCritical = () => {
      loadProductDetail();
      loadCategoryPage();
    };
    const timer = globalThis.setTimeout(preloadCritical, 10000);
    return () => globalThis.clearTimeout(timer);
  }, [location.pathname]);

  return null;
};

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname.startsWith("/admin")) return;
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<StorefrontWrapper><IndexPage /></StorefrontWrapper>} />
        <Route path="/product/:slug" element={<StorefrontWrapper><ProductDetail /></StorefrontWrapper>} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/profile" element={<StorefrontWrapper><Profile /></StorefrontWrapper>} />
        <Route path="/orders/:id" element={<StorefrontWrapper><OrderDetails /></StorefrontWrapper>} />
        <Route path="/checkout" element={<StorefrontWrapper><Checkout /></StorefrontWrapper>} />
        <Route path="/thank-you" element={<StorefrontWrapper><ThankYou /></StorefrontWrapper>} />
        <Route path="/wishlist" element={<StorefrontWrapper><Wishlist /></StorefrontWrapper>} />
        <Route path="/about" element={<StorefrontWrapper><AboutUs /></StorefrontWrapper>} />
        <Route path="/order-procedure" element={<StorefrontWrapper><OrderProcedure /></StorefrontWrapper>} />
        <Route path="/return-policy" element={<StorefrontWrapper><ReturnPolicy /></StorefrontWrapper>} />
        <Route path="/terms-and-conditions" element={<StorefrontWrapper><TermsAndConditions /></StorefrontWrapper>} />
        <Route path="/privacy-policy" element={<StorefrontWrapper><PrivacyPolicy /></StorefrontWrapper>} />
        <Route path="/category/:slug" element={<StorefrontWrapper><CategoryPage /></StorefrontWrapper>} />
        <Route path="/products" element={<StorefrontWrapper><AllProducts /></StorefrontWrapper>} />
        <Route path="/categories" element={<StorefrontWrapper><AllCategories /></StorefrontWrapper>} />
        <Route path="/new-arrivals" element={<StorefrontWrapper><NewArrivals /></StorefrontWrapper>} />
        <Route path="/best-selling" element={<StorefrontWrapper><BestSellingPage /></StorefrontWrapper>} />
        <Route path="/track-order" element={<StorefrontWrapper><TrackOrder /></StorefrontWrapper>} />
        <Route path="/reviews" element={<StorefrontWrapper><Reviews /></StorefrontWrapper>} />
        <Route path="/baby-care-tips" element={<StorefrontWrapper><BabyCareTips /></StorefrontWrapper>} />
        <Route path="/baby-care-tips/:slug" element={<StorefrontWrapper><BabyCareTipDetail /></StorefrontWrapper>} />
        <Route path="/moderator-signup" element={<ModeratorSignup />} />
        <Route path="/moderator" element={<ModeratorAccess />} />
        <Route path="/staff-signup" element={<StaffSignup />} />
        <Route path="/mod-preview-a7f3e2d1" element={<ModeratorPreview />} />
        {isAdminRoute && <Route path="/admin/*" element={<AdminRoutes />} />}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <SiteThemeProvider>
          <LanguageProvider>
            <CartProvider>
              <AppBootEffects />
              <PageViewTracker />
              <RouteChrome />
              <AppRoutes />
            </CartProvider>
          </LanguageProvider>
        </SiteThemeProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;